import { useMemo } from "react";
import { PresenceTableName, useDynamoQuery } from "../../services/dynamo";
import type { PresenceTableItem } from "../../types/presence";
import { useGetItem } from "./use-dynamo-data";

/**
 * Hook per ottenere un singolo item di presenza da DynamoDB tramite chiave primaria (pk) e secondaria (sk).
 * @template P Tipo dell'item di presenza (default: PresenceTableItem)
 * @param {string} pk - Chiave primaria dell'item
 * @param {string} sk - Chiave secondaria dell'item
 * @returns {ReturnType<typeof useGetItem<P, "pk" | "sk">>} Risultato della query (dati, stato, errori)
 */
export const useGetPresence = <P extends PresenceTableItem = PresenceTableItem>(
  pk: string,
  sk: string,
) => useGetItem<P, "pk" | "sk">(PresenceTableName, { pk, sk });

/**
 * Hook per ottenere tutti gli item di presenza che corrispondono a una chiave primaria (pk)
 * e a un prefisso della chiave secondaria (sk), usando una query DynamoDB con begins_with.
 *
 * @template P Tipo dell'item di presenza (default: PresenceItem)
 * @param {P["pk"]} pk - Chiave primaria
 * @param {string} sk - Prefisso della chiave secondaria
 * @returns {ReturnType<typeof useDynamoQuery<P>>} Risultato della query (array di item, stato, errori)
 */
export function useQueryPresencePKSKPrefix<
  P extends PresenceTableItem = PresenceTableItem,
>(pk: P["pk"], sk: string) {
  // Costruisce il comando DynamoDB per la query con begins_with su sk
  const command = useMemo(
    () =>
      ({
        TableName: PresenceTableName,
        KeyConditionExpression: "#pk = :pk AND begins_with(#sk, :sk)",
        ExpressionAttributeValues: {
          ":pk": pk,
          ":sk": sk,
        },
        ExpressionAttributeNames: {
          "#pk": "pk",
          "#sk": "sk",
        },
      }) as const,
    [pk, sk],
  );
  // Esegue la query solo se pk e sk sono definiti
  return useDynamoQuery<P>(command, {
    enabled: pk !== undefined && sk !== undefined,
    // queryKe,
  });
}
